import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import {
  History,
  Plus,
  Phone,
  ArrowRight,
  RotateCcw,
  Edit3,
  Calendar,
  MessageSquare,
  Activity,
} from 'lucide-react'
import { formatDateTime } from '@/utils/format-date'
import { Badge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { Modal } from '@/components/ui/Modal'
import { stageConfig } from '../deals-constants'
import type { DealStage } from '@/types'
import api from '@/services/api'

interface TimelineRow {
  id: string
  source: string
  at: string
  title: string
  description?: string
  author?: string
  details?: string
}

interface UnifiedTimelineEntry {
  id: string
  type: string
  data: string
  description: string
  author?: string
  icon: React.ElementType
  color: string
  details?: string
}

function stageLabel(stage: string) {
  const key = stage.toLowerCase() as DealStage
  return stageConfig[key]?.label ?? stage.replace(/_/g, ' ')
}

function mapTimelineRow(row: TimelineRow): UnifiedTimelineEntry {
  const sourceMeta: Record<string, { label: string; icon: React.ElementType; color: string }> = {
    criacao: { label: 'Criação', icon: Plus, color: '#6366f1' },
    etapa: { label: 'Etapa', icon: ArrowRight, color: '#987de8' },
    alteracao: { label: 'Alteração', icon: Edit3, color: '#64748b' },
    cadencia: { label: 'Cadência BDR', icon: Phone, color: '#f59e0b' },
    reuniao: { label: 'Reunião', icon: Calendar, color: '#e35933' },
    lifecycle: { label: 'Ciclo de vida', icon: RotateCcw, color: '#10b981' },
    interacao: { label: 'Interação', icon: MessageSquare, color: '#3b82f6' },
    atividade: { label: 'Atividade', icon: Activity, color: '#8b5cf6' },
  }

  const meta = sourceMeta[row.source] ?? { label: 'Registro', icon: History, color: '#64748b' }

  let description = row.title
  if (row.source === 'etapa' && row.title.includes('→')) {
    const match = row.title.match(/Etapa:\s*(.+)\s*→\s*(.+)/i)
    if (match) {
      description = `${stageLabel(match[1].trim())} → ${stageLabel(match[2].trim())}`
    }
  } else if (row.description) {
    description = row.description
  }

  return {
    id: row.id,
    type: row.source,
    data: row.at,
    description,
    author: row.author,
    icon: meta.icon,
    color: meta.color,
    details: row.details,
  }
}

export function DealHistoryTab({
  dealId,
  dealEtapa,
  timelinePath,
  onReopenRenegotiation,
}: {
  dealId: string
  dealEtapa?: string
  /** Default: `/deals/:id/timeline`. Conta CS usa `/cs/accounts/:id/timeline`. */
  timelinePath?: string
  onReopenRenegotiation?: () => void
}) {
  const [entries, setEntries] = useState<UnifiedTimelineEntry[]>([])
  const [loading, setLoading] = useState(true)
  const [transcriptionModal, setTranscriptionModal] = useState<string | null>(null)
  const path = timelinePath ?? `/deals/${dealId}/timeline`

  useEffect(() => {
    let cancelled = false
    async function load() {
      setLoading(true)
      try {
        const { data } = await api.get(path)
        const rows: TimelineRow[] = data?.data ?? data ?? []
        const mapped = Array.isArray(rows) ? rows.map(mapTimelineRow) : []
        if (!cancelled) setEntries(mapped)
      } catch {
        if (!cancelled) setEntries([])
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [path])

  const sourceConfig: Record<string, { label: string }> = {
    criacao: { label: 'Criação' },
    etapa: { label: 'Etapa' },
    alteracao: { label: 'Alteração' },
    cadencia: { label: 'Cadência BDR' },
    reuniao: { label: 'Reunião' },
    lifecycle: { label: 'Ciclo de vida' },
    interacao: { label: 'Interação' },
    atividade: { label: 'Atividade' },
  }

  const lostDealBanner =
    dealEtapa === 'perdido' && onReopenRenegotiation ? (
      <div className='mb-4 rounded-lg border border-white/[0.08] bg-white/[0.03] p-4'>
        <p className='text-sm font-medium text-text-primary'>Negociação perdida</p>
        <p className='mt-1 text-xs text-text-muted'>
          O motivo da perda permanece registrado abaixo. Para retomar o pipeline, volte para
          renegociação.
        </p>
        <Button
          variant='primary'
          size='sm'
          className='mt-3'
          icon={<RotateCcw size={13} />}
          onClick={onReopenRenegotiation}
        >
          Voltar para Renegociação
        </Button>
      </div>
    ) : null

  if (loading) {
    return (
      <div>
        {lostDealBanner}
        <div className='flex items-center justify-center py-12'>
          <div className='h-6 w-6 animate-spin rounded-full border-2 border-accent/30 border-t-accent' />
        </div>
      </div>
    )
  }

  if (entries.length === 0) {
    return (
      <div>
        {lostDealBanner}
        <div className='py-12 text-center'>
          <History size={32} className='mx-auto mb-3 text-text-muted opacity-40' />
          <p className='text-sm text-text-muted'>Nenhum histórico desta negociação ainda</p>
          <p className='mt-1 text-xs text-text-muted'>
            Etapas, reuniões, cadência BDR e interações aparecem aqui.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className='space-y-1'>
      {lostDealBanner}
      {entries.map((entry, i) => (
        <motion.div
          key={entry.id}
          initial={{ opacity: 0, x: -8 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: Math.min(i * 0.02, 0.6), duration: 0.2 }}
          className='relative pl-6 pb-6 last:pb-0'
        >
          {i < entries.length - 1 && (
            <div className='absolute bottom-0 left-[8px] top-4 w-px bg-[var(--glass-border)]' />
          )}

          <div
            className='absolute left-0 top-2 flex h-4 w-4 items-center justify-center rounded-full border-2 border-bg-card'
            style={{ backgroundColor: entry.color }}
          >
            <entry.icon size={8} className='text-white' />
          </div>

          <div className='glass-card space-y-1.5 p-3'>
            <div className='flex items-center justify-between gap-2'>
              <Badge variant='muted' className='text-[9px]'>
                {sourceConfig[entry.type]?.label ?? entry.type}
              </Badge>
              <span className='shrink-0 text-caption'>{formatDateTime(entry.data)}</span>
            </div>

            <p className='text-sm text-text-primary'>{entry.description}</p>

            {entry.author && (
              <p className='text-xs text-text-muted'>por {entry.author}</p>
            )}

            {entry.details && entry.type === 'reuniao' && entry.details.length > 80 ? (
              <button
                type='button'
                onClick={() => setTranscriptionModal(entry.details ?? '')}
                className='flex items-center gap-1 text-xs text-accent hover:underline'
              >
                <MessageSquare size={11} />
                Ver transcrição / notas
              </button>
            ) : (
              entry.details && (
                <p className='line-clamp-4 whitespace-pre-wrap text-xs text-text-muted'>
                  {entry.details}
                </p>
              )
            )}
          </div>
        </motion.div>
      ))}

      {transcriptionModal && (
        <TranscriptionViewer text={transcriptionModal} onClose={() => setTranscriptionModal(null)} />
      )}
    </div>
  )
}

function TranscriptionViewer({ text, onClose }: { text: string; onClose: () => void }) {
  return (
    <Modal open onClose={onClose} title='Transcrição da Reunião' size='lg'>
      <div className='space-y-3'>
        <div className='flex items-center gap-2 text-xs text-text-muted'>
          <MessageSquare size={13} />
          <span>Registro da reunião</span>
        </div>
        <div className='max-h-96 overflow-y-auto rounded-lg border border-[var(--glass-border)] bg-[var(--glass-bg)] p-4'>
          <p className='whitespace-pre-wrap text-sm leading-relaxed text-text-primary'>{text}</p>
        </div>
        <div className='flex justify-end'>
          <Button variant='secondary' size='sm' onClick={onClose}>
            Fechar
          </Button>
        </div>
      </div>
    </Modal>
  )
}
